import { CommonModule } from '@angular/common';
import { Component, effect, Injector, input, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  GetContextMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable } from 'rxjs';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import {
  CustomerDeliveryNoteDto,
  CustomerDeliveryNoteService,
  DocLineRole,
  DocLineUpdatedDto,
} from '@digiwall/goliath-documents/proxy';
import { VatSystem, VatType } from '@digiwall/goliath-core/proxy';
import { FieldTypes, IHtmlCellRendererParams } from '@digiwall/ag-grid';
import { ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';
import { ColorizedIconHelper } from '../../../model/colorized-icon.model';

@Component({
  selector: 'document-customer-delivery-note-lines-grid',
  templateUrl: './customer-delivery-note-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class CustomerDeliveryNoteLinesEditorComponent extends BaseThirdPartyDocLineEditor<CustomerDeliveryNoteDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  protected articleLineRole: DocLineRole = DocLineRole.CustomerDeliveryNoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.CustomerDocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;

  public doc = input<CustomerDeliveryNoteDto>();
  public readonly = input<boolean>();

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticleSellableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];
  public actionBtns = [];

  protected override colDef_StockStatusIcons: ColDef = {
    colId: 'stockStatusIcons',
    pinned: 'left',
    width: 40,
    type: FieldTypes.Html,
    cellStyle: (p: any) =>
      p.data?.linkedPreparationId ? { cursor: 'pointer' } : null,
    onCellClicked: (p: any) => {
      if (p.data?.linkedPreparationId) {
        this.router.navigate(['../../customer-preparations', p.data.linkedPreparationId], {
          relativeTo: this.route,
        });
      }
    },
    cellRendererParams: {
      src: (p: ICellRendererParams) => {
        if (p.data?.hasActivePreparationLink) {
          return ColorizedIconHelper.toHtml([{ icon: 'digi-out-checklist', color: 'darkorange' }]);
        }
        return '';
      },
    } as IHtmlCellRendererParams,
  };

  constructor(
    localizationService: LocalizationService,
    private customerDeliveryNoteService: CustomerDeliveryNoteService,
    injector: Injector,
    updateQueue: ObserverQueue,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super(localizationService, customerDeliveryNoteService, injector, updateQueue);

    this.actionBtns = [{ ...this.getPrintActionButton(), visibleInReadonly: true }];
    this.initColDefs();

    effect(() => {
      const readonlyInput = this.readonly();
      const docUnblocked = this.doc().isUnblocked;
      this._readonly = readonlyInput && !docUnblocked;
    });

    effect(() => {
      this._document = this.doc();
      this.setLanguageCode();
    });
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.splice(
      this.columnDefs.findIndex(colDef => colDef.colId === BaseThirdPartyDocLineEditor.CODE_COL_ID),
      0,
      this.colDef_StockStatusIcons,
    );
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Sales),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        this.getColdef_StockLocation(DocType.Sales, true),
      ] as Array<ColDef>),
    );
    this.markDimensionFieldsAsNonEditable(this.columnDefs);
  }

  isChapter = line => line.lineRole == this.chapterLineRole;

  /**
   * On customer delivery notes, restrict the gamme/lot lists to values in stock at the
   * document-header site. Disabled when the Stock feature is off or no site is set.
   */
  protected override getLineStockSiteFilter(): { companySiteId?: string } | null {
    if (!this.stockFeature.isEnabled || !this._document?.companySiteId) return null;
    return { companySiteId: this._document.companySiteId };
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.customerDeliveryNoteService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.customerDeliveryNoteService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.customerDeliveryNoteService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.customerDeliveryNoteService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };
}
